import easyAuctionLogo from '../../assets/easyauction.png';
import logisticAuctionImage from '../../assets/logisticAuction.png';
import industrialAuctionImage from '../../assets/industrialAuction.png';
import civilAuctionImage from '../../assets/civilAuction.png';
import aboutUsImage from '../../assets/aboutus.png';
import { API_BASE_URL } from '../../services/apiConfig';
import type { LandingContent, SectionConfig } from '../../services/landingService';

// localStorage key for the last-fetched landing content (see LandingPageSimple).
export const LANDING_CACHE_KEY = 'landingContentCache';

const BACKEND_URL = API_BASE_URL.replace(/\/api$/, '');

// Images that ship with the app. Landing content stores these as `builtin:*`
// tokens (bundled file names change on every build, so paths can't be saved).
export const BUILTIN_IMAGES: Record<string, string> = {
  'builtin:logo': easyAuctionLogo,
  'builtin:logistic': logisticAuctionImage,
  'builtin:industrial': industrialAuctionImage,
  'builtin:civil': civilAuctionImage,
  'builtin:about': aboutUsImage,
};

// Turns a stored image value (builtin token, uploaded /uploads path or full
// URL) into something usable as an <img src> or CSS background.
export const resolveImage = (value: string | undefined | null): string => {
  const v = (value || '').trim();
  if (!v) return '';
  if (BUILTIN_IMAGES[v]) return BUILTIN_IMAGES[v];
  if (/^https?:\/\//i.test(v) || v.startsWith('data:')) return v;
  return `${BACKEND_URL}/${v.replace(/^\//, '')}`;
};

// Safe to drop inside CSS url("...").
export const cssUrl = (value: string): string =>
  `url("${value.replace(/["\\\n\r]/g, (c) => encodeURIComponent(c))}")`;

export const isSafeLink = (link: string): boolean => /^(#|\/|https?:\/\/|mailto:|tel:)/i.test(link.trim());

// Anchor id each section renders with (used by "scroll to" links).
export const sectionAnchor = (section: SectionConfig): string => {
  switch (section.type) {
    case 'hero':
      return 'home';
    case 'about':
      return 'about';
    case 'features':
      return 'features';
    case 'videos':
      return 'video-tutorials';
    case 'how_it_works':
      return 'how-it-works';
    case 'pricing':
      return 'pricing';
    default:
      return section.id;
  }
};

export const SECTION_LABELS: Record<string, string> = {
  hero: 'Hero banner',
  about: 'Who We Are',
  features: 'Features',
  videos: 'Video Tutorials',
  how_it_works: 'How It Works',
  pricing: 'Pricing',
  custom: 'Custom section',
};

// YouTube / Vimeo page links -> embeddable player URLs. Anything else must
// already be an https embed URL.
export const toEmbedUrl = (url: string): string => {
  const u = (url || '').trim();
  const yt = u.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return /^https:\/\//i.test(u) ? u : '';
};

export const DEFAULT_LANDING_CONTENT: LandingContent = {
  nav: {
    logo: 'builtin:logo',
    logoAlt: 'EasyEAuction logo',
    showTrialButton: true,
    trialText: 'Free Trial',
    trialLink: '/login',
    loginText: 'Login',
    loginLink: '/login',
  },
  announcement: {
    enabled: true,
    text: 'SMS service is under maintenance. For now, login codes (OTP) and notifications are sent to your registered email \u2014 please check your inbox and spam folder. SMS will be back soon.',
  },
  sections: [
    { id: 'hero', type: 'hero', enabled: true },
    { id: 'about', type: 'about', enabled: true },
    { id: 'features', type: 'features', enabled: false },
    { id: 'videos', type: 'videos', enabled: false },
    { id: 'how_it_works', type: 'how_it_works', enabled: true },
    { id: 'pricing', type: 'pricing', enabled: true },
  ],
  hero: {
    slideIntervalSec: 6,
    slides: [
      {
        image: 'builtin:logistic',
        alt: 'Truck hauling an excavator at a container terminal',
        titleLead: ['Your Trusted', 'Auction Platform for'],
        titleHighlight: 'Logistics & Heavy Equipment',
        subtitle:
          'Buy and sell trucks, construction equipment, industrial machinery and logistics assets through a secure, transparent and professional auction platform.',
      },
      {
        image: 'builtin:industrial',
        alt: 'Industrial refinery plant at sunset',
        titleLead: ['Surplus Industrial', 'Assets Sold at'],
        titleHighlight: 'Real Market Value',
        subtitle:
          'List idle plant machinery, processing units and factory equipment to a vetted network of industrial buyers competing in real time.',
      },
      {
        image: 'builtin:civil',
        alt: 'Tower cranes and an excavator on a construction site at sunset',
        titleLead: ['Civil & Construction', 'Equipment Auctions'],
        titleHighlight: 'Built for Contractors',
        subtitle:
          'Move excavators, cranes, loaders and project surplus between sites fast, with transparent bidding and verified contractors on both sides.',
      },
    ],
    primaryButton: { text: 'Browse Auctions', link: '/login' },
    secondaryButton: { enabled: true, text: 'Watch Video', link: '' },
    stats: [
      { icon: 'Radio', number: '500+', label: 'Live Auctions' },
      { icon: 'Users', number: '10,000+', label: 'Registered Bidders' },
      { icon: 'ShieldCheck', number: '100%', label: 'Secure & Transparent' },
      { icon: 'Boxes', number: 'Wide Range', label: 'Heavy Equipment' },
    ],
    categories: [
      {
        title: 'Construction Equipment',
        image:
          'https://images.unsplash.com/photo-1580901368919-7738efb0f87e?q=80&w=900&auto=format&fit=crop',
        link: '',
      },
      {
        title: 'Commercial Vehicles',
        image:
          'https://images.unsplash.com/photo-1519003722824-194d4455a60c?q=80&w=900&auto=format&fit=crop',
        link: '',
      },
      {
        title: 'Industrial Machinery',
        image:
          'https://images.unsplash.com/photo-1567789884554-0b844b597180?q=80&w=900&auto=format&fit=crop',
        link: '',
      },
      {
        title: 'Logistics Assets',
        image:
          'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?q=80&w=900&auto=format&fit=crop',
        link: '',
      },
    ],
  },
  about: {
    title: 'Who We Are',
    paragraphs: [
      'EasyEAuction makes online auctions simple. Auctioneers can create auctions, invite selected participants, manage bids, and download auction reports—all in one place.',
      'Whether you are buying, sourcing suppliers, or participating in an auction, EasyEAuction helps make bidding faster, easier, and more organized.',
    ],
    image: 'builtin:about',
    imageAlt:
      'Auctioneers and bidders reviewing heavy equipment lined up for a live EasyEAuction auction',
    chips: [
      { icon: 'Gavel', text: 'Create Auctions' },
      { icon: 'Users', text: 'Invite Participants' },
      { icon: 'TrendingUp', text: 'Manage Bids' },
      { icon: 'Download', text: 'Download Reports' },
    ],
  },
  features: {
    title: 'Powerful Features',
    subtitle: 'Everything you need to run professional online auctions',
    items: [
      {
        icon: 'Gavel',
        title: 'Professional Auction Creation',
        description:
          'Set up detailed auctions with participant management, document uploads, and automated notifications in minutes.',
      },
      {
        icon: 'Clock',
        title: 'Real-Time Bidding Engine',
        description:
          'Live countdown timers, instant bid updates, and automatic auction extensions for fair competition.',
      },
      {
        icon: 'Users',
        title: 'Dual Role System',
        description:
          'Switch seamlessly between auctioneer and participant roles with a single account and dashboard.',
      },
      {
        icon: 'Shield',
        title: 'Secure Authentication',
        description:
          'OTP-based login system ensures security and privacy for all auction participants and auctioneers.',
      },
      {
        icon: 'TrendingUp',
        title: 'Advanced Analytics',
        description:
          'Detailed reports, bid summaries, and performance analytics available immediately after auction completion.',
      },
      {
        icon: 'CheckCircle',
        title: 'Flexible Pricing Plans',
        description:
          'Pay per auction, or subscribe monthly or yearly — pick whatever fits how often you sell. Always free for bidders and participants.',
      },
    ],
  },
  videos: {
    title: 'Video Tutorials',
    subtitle: 'Learn how to use our platform with these step-by-step video guides',
    items: [
      { title: 'How to Login', url: 'https://www.youtube.com/embed/fEXe6qXROxo' },
      { title: 'How to Create Auction', url: 'https://www.youtube.com/embed/xj06C77Mjgo' },
      { title: 'Participant Use', url: 'https://www.youtube.com/embed/xU_CBEGcgMc' },
    ],
  },
  howItWorks: {
    title: 'How It Works',
    subtitle: 'Get started with our platform in just a few simple steps',
    steps: [
      {
        title: 'Register as a Bidder',
        description: 'Create your account and get verified in minutes to start bidding on live auctions.',
      },
      {
        title: 'Browse & Inspect Listings',
        description: 'Explore detailed listings with specifications, photos and inspection reports.',
      },
      {
        title: 'Place Your Bids',
        description: 'Bid in real-time with live countdown timers and instant bid confirmations.',
      },
      {
        title: 'Win & Complete Purchase',
        description: 'Winning bidders complete secure payment and coordinate pickup or delivery.',
      },
    ],
  },
  pricing: {
    title: 'Simple, Transparent Pricing',
    subtitle:
      'Pay only for the auction you run, or subscribe monthly or yearly if you sell often. No hidden fees.',
    footnote:
      'Prices shown are indicative and exclude applicable taxes. Contact us for custom or enterprise pricing.',
    ctaLink: '/register',
    extraPlan: {
      enabled: true,
      name: 'Enterprise',
      tagline: 'Channel partners & resellers',
      price: 'Custom',
      period: 'pricing',
      badge: 'Channel Partner',
      cta_text: 'Contact Sales',
      cta_link: '/contact-sales',
      features: [
        'White-label platform',
        'API & custom integrations',
        'Volume discounts & rewards',
        'Dedicated SLA support',
      ],
    },
  },
  contactSales: {
    subheading: 'Talk to Your Channel Partner',
    description:
      'Our channel partners help you set up and run your auctions on EasyEAuction. Call or email your channel partner directly and they will guide you through the next steps.',
    highlights: [
      'Help creating and running your auction',
      'Guidance for bidders and participants',
      'Direct phone and email contact',
    ],
    contactName: 'EasyEAuction Partnerships',
    contactRole: 'Channel Partner',
    companyName: 'EasyEAuction',
    phone: '+91 82618 56257',
    email: 'sales.easyeauction@gmail.com',
  },
  customSections: {},
  footer: {
    logo: 'builtin:logo',
    cta: {
      enabled: true,
      title: 'Ready to start bidding?',
      text: 'Create a free account and get access to live industrial, logistics and civil equipment auctions today.',
      buttonText: 'Get Started',
      buttonLink: '/login',
    },
    tagline:
      'A secure, transparent auction platform for trucks, industrial machinery, civil equipment and logistics assets — built for auctioneers and bidders alike.',
    email: 'sales.easyeauction@gmail.com',
    phone: '+91 82618 56257',
    address: 'Mumbai, Maharashtra, India',
    columns: [
      {
        title: 'Platform',
        links: [
          { label: 'About Us', kind: 'section', target: 'about' },
          { label: 'Features', kind: 'section', target: 'features' },
          { label: 'How It Works', kind: 'section', target: 'how-it-works' },
          { label: 'Pricing', kind: 'section', target: 'pricing' },
          { label: 'Video Tutorials', kind: 'section', target: 'video-tutorials' },
        ],
      },
      {
        title: 'Categories',
        links: [
          { label: 'Construction Equipment', kind: 'text', target: '' },
          { label: 'Commercial Vehicles', kind: 'text', target: '' },
          { label: 'Industrial Machinery', kind: 'text', target: '' },
          { label: 'Logistics Assets', kind: 'text', target: '' },
        ],
      },
      {
        title: 'Account',
        links: [
          { label: 'Login', kind: 'route', target: '/login' },
          { label: 'Terms & Conditions', kind: 'terms', target: '' },
        ],
      },
    ],
    copyright: '© 2025 EasyEAuction. All rights reserved.',
    showStats: true,
  },
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

// Lays saved content over the defaults, so a field added to the defaults later
// still shows up for pages saved before it existed. Arrays are taken whole.
function mergeInto<T>(def: T, val: unknown): T {
  if (val === undefined || val === null) return def;
  if (Array.isArray(def)) return (Array.isArray(val) ? val : def) as unknown as T;
  if (isPlainObject(def)) {
    if (!isPlainObject(val)) return def;
    const keys = Object.keys(def);
    if (keys.length === 0) return val as T; // free-form record (customSections)
    const out: Record<string, unknown> = {};
    keys.forEach((k) => {
      out[k] = mergeInto((def as Record<string, unknown>)[k], val[k]);
    });
    return out as T;
  }
  return (typeof val === typeof def ? val : def) as T;
}

export const mergeLandingContent = (stored: Partial<LandingContent> | null | undefined): LandingContent =>
  mergeInto(DEFAULT_LANDING_CONTENT, stored);
